import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Home as HomeIcon,
  TrafficCone,
  Scale,
  FileUser,
  PenLine,
  ArrowRight,
  Clock,
  ShieldCheck,
  GraduationCap,
  FileCheck2,
} from "lucide-react";
import { CATEGORIES, IMAGES, CONTACT } from "@/data/site";
import ReassuranceLine from "@/components/ReassuranceLine";
import { VIEWPORT_ONCE } from "@/lib/motion";

const ICONS = { Home: HomeIcon, TrafficCone, Scale, FileUser, PenLine };

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08 },
  }),
};

export default function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#0B1B3D]/85" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-36">
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            className="max-w-3xl"
          >
            <span className="section-label text-[#D4AF37]">
              Draft &amp; Defend &middot; A Division of Summers Administrative Group
            </span>
            <h1 className="mt-6 font-serif text-4xl font-bold leading-[1.1] tracking-tight text-white text-balance sm:text-5xl lg:text-6xl">
              MBA-Level Document Preparation for Eviction Answers, Debt Lawsuit Filings &amp;
              ATS-Optimized Resumes.
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-slate-200">
              We professionally format your narrative. You provide the facts — we type, structure,
              and polish court-ready, well-organized documents.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/services"
                data-testid="hero-services-button"
                className="group inline-flex items-center gap-2 rounded-md bg-[#D4AF37] px-7 py-3.5 font-semibold text-[#0B1B3D] transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-[#e7c96a]"
              >
                Explore Services
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <a
                href={CONTACT.phoneHref}
                data-testid="hero-phone-button"
                className="inline-flex items-center gap-2 rounded-md border border-white/30 px-7 py-3.5 font-semibold text-white transition-colors duration-200 hover:border-[#D4AF37] hover:text-[#D4AF37]"
              >
                Call {CONTACT.phone}
              </a>
            </div>
            <div className="mt-8 flex items-center gap-2 text-sm text-slate-300">
              <Clock className="h-4 w-4 text-[#D4AF37]" />
              <span className="font-medium tracking-wide">24 Hour Delivery on most documents</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Reassurance */}
      <section className="bg-white">
        <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
          <ReassuranceLine testId="home-reassurance-line" />
        </div>
      </section>

      {/* Value props */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                icon: GraduationCap,
                title: "MBA-Level Precision",
                body: "Founded by a business professional with an MBA and deep experience in research, analytical writing, and administrative structuring.",
              },
              {
                icon: FileCheck2,
                title: "Court-Ready Formatting",
                body: "Clean, organized, professionally formatted documents prepared entirely at your direction and ready for your review.",
              },
              {
                icon: ShieldCheck,
                title: "Discretion & Respect",
                body: "Professionalism, discretion, and respect in every client interaction — for self-represented individuals who need clarity.",
              },
            ].map((v, i) => (
              <motion.div
                key={v.title}
                custom={i}
                initial="hidden"
                whileInView="show"
                  viewport={VIEWPORT_ONCE}
                  variants={fadeUp}
                  className="rounded-lg border border-slate-200 bg-[#F8FAFC] p-8"
              >
                <v.icon className="h-8 w-8 text-[#D4AF37]" />
                <h3 className="mt-4 font-serif text-xl font-semibold text-[#0B1B3D]">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{v.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service categories */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="max-w-2xl">
            <span className="section-label text-[#D4AF37]">What We Prepare</span>
            <h2 className="mt-4 font-serif text-3xl font-bold tracking-tight text-[#0B1B3D] sm:text-4xl">
              Document preparation across the situations that matter most.
            </h2>
            <p className="mt-4 text-slate-600">
              Select a category to view detailed offerings and current pricing on our Services page.
            </p>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {CATEGORIES.map((cat, i) => {
              const Icon = ICONS[cat.icon];
              return (
                <motion.div
                  key={cat.key}
                  custom={i}
                  initial="hidden"
                  whileInView="show"
                  viewport={VIEWPORT_ONCE}
                  variants={fadeUp}
                >
                  <Link
                    to="/services"
                    data-testid={`category-card-${cat.key}`}
                    className="group block overflow-hidden rounded-lg border border-slate-200 bg-white transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={cat.image}
                        alt={cat.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3D]/70 to-transparent" />
                      <div className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center rounded-md bg-white/95 shadow-sm">
                        <Icon className="h-5 w-5 text-[#0B1B3D]" />
                      </div>
                    </div>
                    <div className="p-7">
                      <h3 className="font-serif text-xl font-semibold text-[#0B1B3D]">{cat.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">{cat.blurb}</p>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B1B3D] transition-colors group-hover:text-[#D4AF37]">
                        View services
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-[#0B1B3D]">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-4 py-16 sm:px-6 lg:flex-row lg:px-8">
          <div>
            <h2 className="font-serif text-3xl font-semibold text-white">
              Ready to get your document prepared?
            </h2>
            <p className="mt-2 text-slate-300">
              24-hour delivery on most documents. Call us or send a message today.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="cta-contact-button"
              className="rounded-md bg-[#D4AF37] px-7 py-3.5 font-semibold text-[#0B1B3D] transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-[#e7c96a]"
            >
              Get in Touch
            </Link>
            <Link
              to="/services"
              className="rounded-md border border-white/30 px-7 py-3.5 font-semibold text-white transition-colors duration-200 hover:border-[#D4AF37] hover:text-[#D4AF37]"
            >
              See Pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
